// src/components/BoardPack/pages/consolidate_boardpack/PL_Report.jsx
import React, { useEffect, useRef, useState } from "react";
import { useQueue } from "@/context/QueueContext";
import { useNavigate } from "react-router-dom";
import ErrorState from "@/components/common/ErrorStateComponent";
import LoadingComponent from "@/components/common/LoadingComponent";
import { apiCall } from "@/api/apiCall";
import { processParams } from "../../utils/paramsHelper";
import PL_Report_component from "../../components/consolidate_boardpack_components/PL_Report_component";

const buildPeriodBlock = (block, leftPeriodLabel, rightPeriodLabel, varianceLabel) => {
  if (!block) return undefined;

  const rows = (block.rows || []).map((r) => ({
    kind: "normal",
    label: r.label,
    values: r.values,
  }));

  // headerContracts lists each country once per period block plus a trailing
  // variance label (e.g. 6 countries x 2 blocks + "ALL COMPANIES" = 13 entries).
  const contribution = block.contribution;
  const contractsNoVariance = (block.headerContracts || []).slice(0, -1);
  const countryColumns = contribution?.headerContracts?.length
    ? contribution.headerContracts
    : contractsNoVariance.slice(0, contractsNoVariance.length / 2);

  if (contribution?.rows?.length) {
    rows.push({
      kind: "sectionHeader",
      label: "% Contribution by Country",
      values: [...countryColumns, ...countryColumns],
    });
    contribution.rows.forEach((r) => {
      rows.push({
        kind: "normal",
        noVariance: true,
        label: r.label,
        values: [...r.values, ...r.values, ""],
      });
    });
  }

  return { leftPeriodLabel, rightPeriodLabel, varianceLabel, countryColumns, rows };
};

const buildPLData = (data) => {
  if (!data) return { pyData: undefined, budgetData: undefined };
  const { period, year, prevYear, block1, block2 } = data;
  return {
    pyData: buildPeriodBlock(block1, `${period} ${year}`, `${period} ${prevYear}`, `${year} V ${prevYear}`),
    budgetData: buildPeriodBlock(block2, `${period} ${year}`, `${period} ${year}`, "Bud V Actual"),
  };
};

const PL_Report = ({ year = "", month = "", contract = "", page = null, isRefresh = false }) => {
  const { enqueue } = useQueue();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableData, setTableData] = useState(null);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!year || !month || !contract) return;
    if (hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    fetchData();
  }, [year, month, contract]);
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiParams = processParams(page?.params || {}, { year, month, contract });

      const response = await enqueue(() =>
        apiCall("GET", "/boardpack/consolidate", apiParams, navigate, false, false, isRefresh)
      );

      if (!response?.success) {
        throw new Error(response?.message || "API returned unsuccessful response");
      }

      setTableData(response.data);
      console.log(response.data);
    } catch (err) {
      console.error("Profit & Loss fetch error:", err);
      setError(err.message || "Failed to load data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingComponent />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchData} />;
  }

  const { pyData, budgetData } = buildPLData(tableData);

  return (
    <PL_Report_component
      title={page?.title}
      pyData={pyData}
      budgetData={budgetData}
      contract={contract}
      year={year}
      month={month}
    />
  );
};

export default PL_Report;
